import { UzBadge, UzButton, UzCard, UzDatePicker, UzInput, UzSwitch } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { useBacklogDrip, useRunBacklogDrip, useUpdateBacklogDrip } from '../use-leads';

import type { BacklogPriority } from '@utazzo/shared';

import { useCurrentUser } from '@/features/auth/use-auth';

const PRIORITY_LABEL: Record<BacklogPriority, string> = {
  HOT: 'Hot',
  WARM: 'Warm',
  PROPOSAL: 'Proposal',
  ACTIVE: 'Active (other)',
  NQ_CONTACTED: 'NQ, contacted',
  NQ_UNCONTACTED: 'NQ, never contacted',
};
const PRIORITY_ORDER: BacklogPriority[] = [
  'HOT',
  'WARM',
  'PROPOSAL',
  'ACTIVE',
  'NQ_CONTACTED',
  'NQ_UNCONTACTED',
];

const isoDate = (d: Date | null): string | null =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : null;
const fromIso = (s: string): Date => {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
};

const Stat = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: React.ReactNode;
  hint?: string;
}): React.JSX.Element => (
  <div className="rounded-md border border-zinc-200 bg-white px-3 py-2">
    <div className="text-[11px] text-zinc-500">{label}</div>
    <div className="text-xl font-semibold tabular-nums">{value}</div>
    {hint && <div className="text-[11px] text-zinc-400">{hint}</div>}
  </div>
);

/**
 * How the exit-unassigned queue is being dealt out: what is left (Hot
 * first, never-contacted NQ last), today's quota against the clear-by date,
 * who got what today, and - for admins - the switch, the date, the per-agent
 * ceiling and the WhatsApp follow-up toggle. Sits above the queue grid on
 * the Unassigned page.
 */
export const BacklogDripPanel = (): React.JSX.Element | null => {
  const me = useCurrentUser();
  const isAdmin = me?.role === 'ADMIN';
  const summary = useBacklogDrip();
  const save = useUpdateBacklogDrip();
  const run = useRunBacklogDrip();

  const data = summary.data;
  const [enabled, setEnabled] = useState(false);
  const [clearBy, setClearBy] = useState<string>('');
  const [ceiling, setCeiling] = useState('8');
  const [followup, setFollowup] = useState(true);

  useEffect(() => {
    if (!data) return;
    setEnabled(data.config.enabled);
    setClearBy(data.config.clearByDate);
    setCeiling(String(data.config.perAgentDailyCeiling));
    setFollowup(data.config.sendFollowupTemplate);
  }, [data]);

  if (summary.isError || (!summary.isLoading && !data)) return null;
  if (!data) {
    return (
      <UzCard title="Backlog drip">
        <p className="text-sm text-zinc-500">Loading…</p>
      </UzCard>
    );
  }

  const ceilingNum = Number(ceiling);
  const dirty =
    enabled !== data.config.enabled ||
    clearBy !== data.config.clearByDate ||
    ceilingNum !== data.config.perAgentDailyCeiling ||
    followup !== data.config.sendFollowupTemplate;
  const ceilingOk = Number.isInteger(ceilingNum) && ceilingNum >= 1 && ceilingNum <= 100;

  const onSave = (): void => {
    if (!ceilingOk || !clearBy) return;
    save.mutate({
      enabled,
      clearByDate: clearBy,
      perAgentDailyCeiling: ceilingNum,
      sendFollowupTemplate: followup,
    });
  };

  const left = data.remaining.total;
  const pct =
    data.today.quota > 0
      ? Math.min(100, Math.round((data.today.assigned / data.today.quota) * 100))
      : 0;

  return (
    <UzCard
      title={
        <span className="flex items-center gap-2">
          Backlog drip
          <UzBadge tone={data.config.enabled ? 'success' : 'outline'}>
            {data.config.enabled ? 'On' : 'Off'}
          </UzBadge>
        </span>
      }
      description={
        left === 0
          ? 'The queue is empty.'
          : `${left} lead${left === 1 ? '' : 's'} left to deal out by ${data.config.clearByDate}: ${data.workingDaysLeft} working day${
              data.workingDaysLeft === 1 ? '' : 's'
            }, so ${data.today.quota} today. Hot and Warm go first, never-contacted Not Qualified last; each lead goes to an online expert for its destination.`
      }
      actions={
        isAdmin ? (
          <UzButton
            size="sm"
            variant="outline"
            onClick={() => run.mutate()}
            disabled={run.isPending || left === 0}
          >
            {run.isPending ? 'Dealing…' : 'Run now'}
          </UzButton>
        ) : undefined
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Stat label="Left in queue" value={left} />
          <Stat
            label="Needs manual assign"
            value={data.remaining.needsManual}
            hint="No destination expert mapped"
          />
          <Stat label="Working days left" value={data.workingDaysLeft} />
          <Stat
            label="Today"
            value={`${data.today.assigned} / ${data.today.quota}`}
            hint={`${pct}% of today's quota dealt out`}
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <div className="mb-1 text-xs font-medium text-zinc-500">Left, in the order dealt</div>
            <table className="w-full text-sm">
              <tbody>
                {PRIORITY_ORDER.map((p) => (
                  <tr key={p} className="border-b border-zinc-100 last:border-0">
                    <td className="py-1 text-zinc-700">{PRIORITY_LABEL[p]}</td>
                    <td className="py-1 text-right tabular-nums">
                      {data.remaining.byPriority[p] ?? 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="mb-1 text-xs font-medium text-zinc-500">Dealt out today</div>
            {data.today.byAgent.length === 0 ? (
              <p className="text-sm text-zinc-400">
                {data.lastAssignedAt
                  ? 'Nothing yet today.'
                  : 'Nothing dealt out yet. Switch the drip on, or press Run now.'}
              </p>
            ) : (
              <table className="w-full text-sm">
                <tbody>
                  {data.today.byAgent.map((a) => (
                    <tr key={a.agentId} className="border-b border-zinc-100 last:border-0">
                      <td className="py-1 text-zinc-700">
                        {a.fullName}{' '}
                        {!a.online && (
                          <span className="text-[11px] text-zinc-400">(not online now)</span>
                        )}
                      </td>
                      <td className="py-1 text-right tabular-nums">{a.assigned}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>

        {isAdmin && (
          <div className="grid gap-4 border-t border-zinc-100 pt-4 sm:grid-cols-2 lg:grid-cols-4">
            <UzSwitch
              id="backlog-drip-enabled"
              label="Deal out automatically"
              description="Every 30 minutes, 10:00-16:00 IST, Monday to Saturday."
              checked={enabled}
              onCheckedChange={setEnabled}
            />
            <UzDatePicker
              label="Clear by"
              hint="Daily quota is recomputed from what is left."
              value={clearBy ? fromIso(clearBy) : null}
              onValueChange={(d) => setClearBy(isoDate(d) ?? '')}
            />
            <UzInput
              label="Max per agent per day"
              inputMode="numeric"
              value={ceiling}
              onChange={(e) => setCeiling(e.target.value)}
              error={ceilingOk ? undefined : 'Between 1 and 100'}
              hint="On top of their fresh leads."
            />
            <UzSwitch
              id="backlog-drip-followup"
              label="WhatsApp follow-up on hand-out"
              description="Sent once per lead, from the new owner."
              checked={followup}
              onCheckedChange={setFollowup}
            />
            <div className="sm:col-span-2 lg:col-span-4">
              <UzButton
                onClick={onSave}
                disabled={!dirty || !ceilingOk || !clearBy || save.isPending}
              >
                {save.isPending ? 'Saving…' : 'Save drip settings'}
              </UzButton>
            </div>
          </div>
        )}
      </div>
    </UzCard>
  );
};
